import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import type { SearchResult, SearchResultType } from '@tailoring-crm/shared';
import { useGlobalSearch } from '../../lib/search/use-search.js';

/**
 * One box, in the header, on every screen.
 *
 * A shop does not think in modules. Somebody rings up and says "Priya, the green lehenga" or
 * reads out an order number, and until now answering either meant knowing WHICH SCREEN that
 * lived on before you could start looking.
 *
 * Three things this does that a plain input would not:
 *
 * **It opens the result, not the module.** Every hit carries its own route, so finding and
 * doing are one gesture.
 *
 * **It says how many there really are.** "5 of 63 customers" tells somebody to type more; a
 * silent five tells them there are five.
 *
 * **It closes on Escape and on choosing.** A results panel that stays open over the screen
 * somebody just navigated to is a panel they have to dismiss before they can work.
 */
export function GlobalSearch(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  const results = useGlobalSearch(query, { enabled: open });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const choose = (result: SearchResult): void => {
    setOpen(false);
    setQuery('');
    navigate(result.route);
  };

  const groups = results.data?.groups ?? [];
  const hasQuery = query.trim().length >= 2;

  return (
    <div ref={container} className="relative hidden min-w-0 flex-1 sm:block sm:max-w-md">
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-sidebar-foreground"
      />
      <input
        type="search"
        role="searchbox"
        value={query}
        aria-label={t('search.label')}
        placeholder={t('search.placeholder')}
        className="h-9 w-full rounded-md border border-sidebar-border bg-sidebar-panel pl-9 pr-8 text-sm text-sidebar-accent-foreground placeholder:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent"
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
            setQuery('');
          }
        }}
      />
      {query ? (
        <button
          type="button"
          aria-label={t('search.clear')}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-sidebar-foreground hover:text-sidebar-accent-foreground"
          onClick={() => {
            setQuery('');
            setOpen(false);
          }}
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      ) : null}

      {open && hasQuery ? (
        <div
          role="listbox"
          aria-label={t('search.label')}
          className="absolute left-0 right-0 top-full z-40 mt-1 max-h-96 overflow-y-auto rounded-md border border-border bg-background p-1 shadow-lg"
        >
          {groups.length === 0 && !results.isFetching ? (
            <p className="p-3 text-sm text-muted-foreground">{t('search.noResults')}</p>
          ) : null}

          {groups.map((group) => (
            <div key={group.type} className="py-1">
              <p className="px-2 pb-1 text-xs font-medium text-muted-foreground">
                {t(`search.type.${group.type as SearchResultType}`)}
                {/* The true count, which is what tells somebody to type more. */}
                {group.total > group.items.length
                  ? ` · ${t('search.showingOf', { shown: group.items.length, total: group.total })}`
                  : ''}
              </p>
              {group.items.map((item) => (
                <button
                  key={`${item.type}-${item.id}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="flex w-full flex-col rounded px-2 py-1.5 text-left hover:bg-accent"
                  onClick={() => choose(item)}
                >
                  <span className="truncate text-sm font-medium">{item.title}</span>
                  {item.subtitle ? (
                    <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span>
                  ) : null}
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default GlobalSearch;
